import { setSnackbar } from '@/helpers';
import {
    getEmployees,
    addEmployee,
    deleteEmployee,
    updateEmployee,
    inviteEmployee,
    importEmployees,
} from '../../Api/ApiEmployees';
import { postRequest, apiDomainEmploy, updateRequest } from '../../endpoints';

const state = {
    employees: [],
    employees_all: [],
    selectedEmployees: [],
    filter: {
        search: '',
        sort_by: 0,
        eligibility: 0,
        searchKeys: [],
    },
    upload_employees_file: null,
    employee: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: {},
        national_identity_number: '',
        foreign_national_identity_number: false,
        employee_note: '',
    },
};

const mutations = {
    SET_EMPLOYEES(state, employees) {
        state.employees = employees;
        state.employees_all = employees;

        state.employees.forEach((employee) => {
            employee.name = `${employee.first_name} ${employee.last_name}`;

            const string = `
                ${employee.first_name ?? ''}
                ${employee.last_name ?? ''}
                ${employee.email ?? ''}
                ${employee?.phone ?? ''}
                ${employee?.role?.name ?? ''}
                ${employee.national_identity_number ?? ''}
                ${employee.profile.employee_note ?? ''}
            `;

            employee.search_query_string = string
                .trim()
                .replace(/\s+/g, ' ')
                .replace(/<[^>]*>/g, '')
                .toLowerCase();
        });
    },
    SET_SELECTED_EMPLOYEES(state, employee) {
        state.selectedEmployees = employee;
    },
    SET_EMPLOYEE(state, employee) {
        state.employee = employee;
    },
    STATE_UPDATE_EMPLOYEE(state, field) {
        const key = Object.keys(field)[0];
        state.employee[key] = field[key];
    },
    SET_EMPLOYEES_SORT_BY(state, data) {
        state.filter.sort_by = data.type;
        state.employees = data.employees.sort((a, b) => {
            if (!a || !b) {
                return 1;
            }

            if (data.type === 0) {
                // A-Ö
                if (!a.first_name || !b.first_name) {
                    return 1;
                }

                if (!a.first_name || !b.first_name) {
                    return 1;
                }

                return a.first_name.localeCompare(b.first_name, 'sv');
            } else if (data.type === 1) {
                // Ö-A
                if (!a.first_name || !b.first_name) {
                    return 1;
                }

                if (!a.first_name || !b.first_name) {
                    return 1;
                }

                return b.first_name.localeCompare(a.first_name, 'sv');
            } else if (data.type === 2) {
                // Tillagd Senast
                if (!a.id || !b.id) {
                    return 1;
                }
                return b.id - a.id;
            } else if (data.type === 3) {
                // Tillagd Först
                if (!a.id || !b.id) {
                    return 1;
                }
                return a.id - b.id;
            }
        });
    },
    SET_EMPLOYEES_ELIGIBILITY(state, data) {
        state.filter.eligibility = data.field;

        if (!data.field) {
            return (state.employees = state.employees_all);
        }
        if (data.field === 1) {
            return (state.employees = data.employees.filter((employee) => employee.role.id === 2));
        } else {
            return (state.employees = data.employees.filter((employee) => employee.role.id > 2));
        }
    },
    SEARCH(state, data) {
        state.filter.search = data.searchWord;
        state.filter.searchKeys = data.searchKeys;

        let result = [];

        data.searchKeys.forEach((key) => {
            const expression = key.replace(/([^.]*)./, ''); //removes everything before first dot + .
            result = [
                ...result,
                ...data.employees.filter(
                    (employee) =>
                        expression.split('.').reduce((obj, i) => (obj[i] ? obj[i] : false), employee) ===
                        data.searchWord
                ),
            ];
        });

        state.employees = result;
    },
    SET_UPLOAD_EMPLOYEES(state, file) {
        state.upload_employees_file = file;
    },
};

const actions = {
    async GET_EMPLOYEES({ commit }) {
        await getEmployees().then((response) => {
            if (response.status === 'Error') {
                return;
            }
            if (response.status === 'Success') {
                commit('SET_EMPLOYEES', response.data);
            }
        });
    },
    async ADD_EMPLOYEE({ commit, dispatch, state }, payload) {
        await addEmployee(payload).then((response) => {
            dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
            commit('GlobalStore/SET_SAVE_STATUS', response.status, { root: true });

            if (response.status === 'Error') {
                return false;
            }

            const newEmployee = { ...response.data, is_new: true };
            commit('SET_EMPLOYEES', [...state.employees, { ...newEmployee }]);

            if (payload?.team_ids) {
                dispatch('SET_TEAMS', {
                    user_profile_id: response.data.profile.id,
                    team_ids: payload.team_ids,
                }).then(() => {
                    commit(
                        'SET_EMPLOYEES',
                        state.employees.map((employee) => {
                            if (employee.id === newEmployee.id) {
                                return {
                                    ...employee,
                                    profile: {
                                        ...employee.profile,
                                        team_ids: payload.team_ids,
                                    },
                                };
                            }
                            return employee;
                        })
                    );
                });
            }

            if (payload?.expertise_ids) {
                dispatch('SET_EXPERTISES', {
                    user_profile_id: response.data.profile.id,
                    expertise_ids: payload.expertise_ids,
                }).then((data) => {
                    if (response.status === 'Error') return;

                    commit(
                        'SET_EMPLOYEES',
                        state.employees.map((employee) => {
                            if (employee.id === newEmployee.id) {
                                return {
                                    ...employee,
                                    expertises: data,
                                    profile: {
                                        ...employee.profile,
                                        expertise_ids: payload.expertise_ids,
                                    },
                                };
                            }
                            return employee;
                        })
                    );
                });
            }

            return true;
        });
    },
    async INVITE_EMPLOYEE({ commit, dispatch }, payload) {
        await inviteEmployee(payload).then((response) => {
            dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
            commit('GlobalStore/SET_SAVE_STATUS', response.status, { root: true });

            if (response.status === 'Error') {
                return false;
            }

            const newEmployee = { ...response.data, is_new: true };
            commit('SET_EMPLOYEES', [...state.employees, { ...newEmployee }]);

            if (payload?.team_ids) {
                dispatch('SET_TEAMS', {
                    user_profile_id: response.data.profile.id,
                    team_ids: payload.team_ids,
                }).then(() => {
                    commit(
                        'SET_EMPLOYEES',
                        state.employees.map((employee) => {
                            if (employee.id === newEmployee.id) {
                                return {
                                    ...employee,
                                    profile: {
                                        ...employee.profile,
                                        team_ids: payload.team_ids,
                                    },
                                };
                            }
                            return employee;
                        })
                    );
                });
            }

            if (payload?.expertise_ids) {
                dispatch('SET_EXPERTISES', {
                    user_profile_id: response.data.profile.id,
                    expertise_ids: payload.expertise_ids,
                }).then((data) => {
                    if (response.status === 'Error') return;
                    console.log(data);

                    commit(
                        'SET_EMPLOYEES',
                        state.employees.map((employee) => {
                            if (employee.id === newEmployee.id) {
                                return {
                                    ...employee,
                                    expertises: data,
                                    profile: {
                                        ...employee.profile,
                                        expertise_ids: payload.expertise_ids,
                                    },
                                };
                            }
                            return employee;
                        })
                    );
                });
            }

            return true;
        });
    },
    async IMPORT_EMPLOYEES({ commit, dispatch, state }) {
        await importEmployees({
            file: state.upload_employees_file,
        }).then((response) => {
            if (response.status === 'Error') {
                dispatch('GlobalStore/SET_SNACKBAR', { ...setSnackbar(response), delayTime: 60000 }, { root: true });
                commit('GlobalStore/SET_SAVE_STATUS', response.status, { root: true });
                return false;
            }
            dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
            commit('GlobalStore/SET_SAVE_STATUS', response.status, { root: true });

            if (response.status === 'Success') {
                let newEmployees = response.data.map((employee) => {
                    return { ...employee, is_new: true };
                });
                commit('SET_EMPLOYEES', [...state.employees, ...newEmployees]);
            }

            return true;
        });
    },
    async UPDATE_EMPLOYEE({ commit, dispatch, state }, data) {
        await updateEmployee(data.employee.profile.id, {
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email,
            phone_number: data.phone_number,
            role_id: data.role_id,
            employee_note: data.employee_note,
        }).then((response) => {
            commit('GlobalStore/SET_SAVE_STATUS', response.status, { root: true });
            if (response.status === 'Error') {
                dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
                return false;
            }

            commit(
                'SET_EMPLOYEES',
                state.employees.map((item) => {
                    if (item.id === data.employee.id) {
                        return { ...item, role: data.employee.role };
                    }
                    return item;
                })
            );

            response.message = 'Medarbetaren har uppdaterats';
            dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
            return response.data;
        });
    },
    async UPDATE_EMPLOYEE_TIME_OFF({ commit, dispatch, state }, payload) {
        return await updateRequest(`${apiDomainEmploy}/employees/${payload.profile_id}/time-off`, {
            ...payload.inactive,
        }).then((response) => {
            dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
            commit('GlobalStore/SET_SAVE_STATUS', response.status, { root: true });

            if (response.status === 'Error') {
                return false;
            }

            commit(
                'SET_EMPLOYEES',
                state.employees.map((item) => {
                    if (item.id === payload.id) {
                        return { ...item, profile: { ...item.profile, inactive: payload.inactive } };
                    }
                    return item;
                })
            );

            return response.data;
        });
    },
    async DELETE_EMPLOYEE({ commit, dispatch, state }, employee) {
        await deleteEmployee(employee.profile.id).then((response) => {
            dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
            commit('GlobalStore/SET_SAVE_STATUS', response.status, { root: true });
            if (response.status === 'Error') {
                return false;
            }
            commit(
                'SET_EMPLOYEES',
                state.employees.filter((item) => item.id !== employee.id)
            );
            return true;
        });
    },
    async SET_TEAMS({ dispatch }, data) {
        const { user_profile_id = null, team_ids = [] } = data;

        if (team_ids.length > 0) {
            return postRequest(`${apiDomainEmploy}/employees/${user_profile_id}/teams`, {
                team_ids: team_ids,
            }).then((response) => {
                if (response.status === 'Error')
                    dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
                return response.data;
            });
        }

        return false;
    },
    async SET_EXPERTISES({ dispatch }, data) {
        const { user_profile_id = null, expertise_ids = [] } = data;

        if (expertise_ids.length > 0) {
            return updateRequest(`${apiDomainEmploy}/employees/${user_profile_id}/expertises`, { expertise_ids }).then(
                (response) => {
                    if (response.status === 'Error') {
                        return dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
                    }
                    return response.data;
                }
            );
        }

        return false;
    },
    async SET_EMPLOYEES_SORT_BY({ state, commit }, type) {
        commit('SET_EMPLOYEES_SORT_BY', { employees: state.employees, type });
    },
    async SORT_EMPLOYEES_ELIGIBILITY({ state, commit }, field) {
        commit('SET_EMPLOYEES_ELIGIBILITY', { employees: state.employees_all, field });
    },
    async SEARCH({ state, commit }, data) {
        commit('SEARCH', { employees: state.employees, ...data });
    },
};

const getters = {
    GET_EMPLOYEES: (state) => state.employees.filter((employee) => employee),
    GET_SELECTED_EMPLOYEES: (state) => state.selectedEmployees,
    GET_EMPLOYEES_LIST: (state) =>
        state.employees.map((employee) => ({
            ...employee,
            user_profile_id: employee.profile.id,
        })),
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
